/**
 * PreferencesContext — Single source of truth for the user-preferences JSON blob.
 *
 * Eliminates the duplicate UIPreferencesApi.get() calls that ThemeContext and
 * TutorialContext used to make independently.  Provides:
 *   • preferences    – the raw blob (or null while loading)
 *   • loading        – true until the first fetch settles
 *   • patchPreferences(partial) – optimistic merge + debounced PATCH to server
 *   • refreshPreferences()      – force re-fetch from server
 *
 * The preferences blob is schemaless on the backend.  PATCH merges only the
 * provided top-level keys — it never replaces the whole object.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { UIPreferencesApi } from '../services/api/UIPreferencesApi';
import { upgradePreferences } from '../utils/preferencesMigration';

const PreferencesContext = createContext({
  preferences: null,
  loading: true,
  patchPreferences: async () => {},
  flushPreferences: async () => {},
  refreshPreferences: async () => {},
});

export const usePreferences = () => useContext(PreferencesContext);

// Debounce interval for batching rapid patches (ms)
const PATCH_DEBOUNCE_MS = 800;

export function PreferencesProvider({ children }) {
  const { user, userId, accessToken } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);

  // Guards
  const fetchedRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const pendingPatchRef = useRef(null); // accumulates keys between debounce flushes
  const lastPersistedRef = useRef(null); // JSON string of last payload sent

  // Resolve effective userId the same way Theme/Tutorial do
  const preferenceUserId = React.useMemo(() => {
    return (user?.id || user?.userId || userId || localStorage.getItem('user_id')) || null;
  }, [user, userId]);

  // ── Bootstrap: one fetch per authenticated session ──────────────────
  useEffect(() => {
    // Only fetch once per session and only when we have both a user and a token
    if (!preferenceUserId || !accessToken || fetchedRef.current) {
      if (!preferenceUserId || !accessToken) setLoading(false);
      return;
    }
    fetchedRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        const rawDto = await UIPreferencesApi.get(preferenceUserId);
        if (cancelled) return;

        // Backend DTO shape: { preferenceId, userId, preferences: {...} }
        const blob = rawDto?.preferences || rawDto || {};

        // Run schema migration (e.g., V1→V2 adds gamification)
        const migration = upgradePreferences(blob);
        const finalBlob = migration.preferences;

        if (migration.upgraded) {
          console.log('[Preferences] Migrated schema', migration.fromVersion, '→', migration.toVersion);
          // Persist upgraded blob back to server (fire-and-forget)
          UIPreferencesApi.patch(preferenceUserId, finalBlob).catch((e) =>
            console.warn('[Preferences] Failed to persist migration:', e?.message || e),
          );
        }

        setPreferences(finalBlob);
      } catch (e) {
        const status = e?.status || e?.response?.status;
        if (status === 404) {
          // No prefs record yet — start with empty blob
          setPreferences({});
        } else {
          console.warn('[Preferences] Fetch failed:', status, e?.message || e);
          setPreferences({}); // graceful fallback
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [preferenceUserId, accessToken]);

  // ── Refresh: allow consumers to force a re-fetch ────────────────────
  const refreshPreferences = useCallback(async () => {
    if (!preferenceUserId) return;
    try {
      const rawDto = await UIPreferencesApi.get(preferenceUserId);
      const blob = rawDto?.preferences || rawDto || {};
      setPreferences(blob);
    } catch (e) {
      console.warn('[Preferences] Refresh failed:', e?.message || e);
    }
  }, [preferenceUserId]);

  // ── Patch: optimistic update + debounced network persistence ────────
  const patchPreferences = useCallback(
    async (partial) => {
      if (!partial || typeof partial !== 'object') return;

      // 1. Optimistic local merge (shallow — preserves sibling keys in blob)
      setPreferences((prev) => ({ ...(prev || {}), ...partial }));

      // 2. Accumulate keys for the debounced batch
      pendingPatchRef.current = { ...(pendingPatchRef.current || {}), ...partial };

      // 3. (Re)start debounce timer
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(async () => {
        const payload = pendingPatchRef.current;
        pendingPatchRef.current = null;
        if (!payload || !preferenceUserId) return;

        const payloadStr = JSON.stringify(payload);
        if (payloadStr === lastPersistedRef.current) return; // skip identical

        try {
          lastPersistedRef.current = payloadStr;
          await UIPreferencesApi.patch(preferenceUserId, payload);
        } catch (e) {
          console.warn('[Preferences] PATCH failed, attempting upsert:', e?.message || e);
          try {
            await UIPreferencesApi.upsert(preferenceUserId, payload);
          } catch (e2) {
            console.error('[Preferences] Upsert fallback also failed:', e2?.message || e2);
          }
        }
      }, PATCH_DEBOUNCE_MS);
    },
    [preferenceUserId],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // ── Flush: immediately send any pending debounced batch ─────────────
  const flushPreferences = useCallback(async () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const payload = pendingPatchRef.current;
    pendingPatchRef.current = null;
    if (!payload || !preferenceUserId) return;

    const payloadStr = JSON.stringify(payload);
    if (payloadStr === lastPersistedRef.current) return;

    try {
      lastPersistedRef.current = payloadStr;
      await UIPreferencesApi.patch(preferenceUserId, payload);
    } catch (e) {
      console.warn('[Preferences] Flush PATCH failed, attempting upsert:', e?.message || e);
      try {
        await UIPreferencesApi.upsert(preferenceUserId, payload);
      } catch (e2) {
        console.error('[Preferences] Flush upsert fallback also failed:', e2?.message || e2);
      }
    }
  }, [preferenceUserId]);

  // Reset when user changes (e.g., logout → login as different user)
  useEffect(() => {
    if (!preferenceUserId) {
      fetchedRef.current = false;
      setPreferences(null);
      setLoading(true);
    }
  }, [preferenceUserId]);

  const value = React.useMemo(
    () => ({ preferences, loading, patchPreferences, flushPreferences, refreshPreferences }),
    [preferences, loading, patchPreferences, flushPreferences, refreshPreferences],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export default PreferencesContext;
